import { isInternetConnected, isAppEnv } from "./CommonUtils";
import axios from "axios";
import { logObj, tokenObj } from "../../mobile/app/constants/Constant";
import { webRefreshTokenRedux } from "./Constants";
import { getEnvironment } from "../utils/EnvConfig";
import store from "../../web/src/SharedContextStore/store";
import { handleModuleFailure } from "../../web/src/SharedContextStore/slices/dwLogTracker";

const instanceAxios = axios.create({
  // baseURL: API_URL,
});

instanceAxios.interceptors.request.use(async (config) => {
  // web app will throgh the error for isInternetConnected
  let isConnected = window.navigator.onLine || (await isInternetConnected());
  if (!isConnected) {
    // __Dev__ is not defined or in used shared so it through the Error in Web App so commented the logger as of now
    //logError("No internet");
    return Promise.reject("No internet");
  }
  // config.baseURL = await getBaseURL()
  if (!config.url) {
    return config;
  }

  var currentUrl = "";
  Object.entries(config?.url || {}).forEach(([k, v]) => {
    if (k !== "apiUrl") {
      currentUrl = `${currentUrl}${k}/${v}`;
    }
  });
  if (!isAppEnv()) {
    // only for web app
    await refreshWebToken(config);
  }


  /**
   * Handled URL replacement for Secure and Non-Auth APIs
   */

  const NAPATH = "iam/acm"
  const SECUREPATH = "digital/secure"
  const reduxStore = store.getState();

  function replaceInUrl(url, replacement) {
    if (typeof url !== 'string') return url;

    return url.replace(/\/digital(?:\/secure)?/, replacement);
  }

  // if (!reduxStore?.brightspeedToken?.headers?.['access-token'] || reduxStore?.brightspeedToken?.headers.flowType === 'NA') {
    
  //   const updatedURL = replaceInUrl(currentUrl, NAPATH)

  //   return {
  //     ...config,
  //     baseURL: config?.url?.apiUrl,
  //     url: updatedURL,
  //   };
  // }

  return {
    ...config,
    baseURL: config?.url?.apiUrl,
    // url: replaceInUrl(currentUrl, "digital", SECUREPATH),
    url: currentUrl
  };
});
instanceAxios.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  async (error) => {
    try {
      if (!isAppEnv()) {
        const status = error?.response?.status;
        const config = error?.config || error?.response?.config;
        const reduxStore = store?.getState();
        const authPhase = reduxStore?.user?.authPhase;
        const hasIamAccessToken =
          !!reduxStore?.brightspeedToken?.headers?.["access-token"];
        const hasUserRefreshToken = !!reduxStore?.user?.refreshToken;
        const isPostLogin =
          authPhase === "postlogin" || hasIamAccessToken || hasUserRefreshToken;
        const responseMessage =
          error?.response?.data?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          "";
        const hasAuthHeader = !!config?.headers?.Authorization;
        const isInvalidAuthMessage =
          typeof responseMessage === "string" &&
          /(invalid|expired).*(authorization|token)|unauthor/i.test(responseMessage);

        if (!isPostLogin && status === 401 && hasAuthHeader && isInvalidAuthMessage) {
          const method = (config?.method || "GET").toUpperCase();
          const apiUrl = config?.url || "unknown-api";
          const flowType =
            reduxStore?.brightspeedToken?.headers?.flowType || "loginFlow";
          const errorMessage = `401 invalid authorization token: ${method} ${apiUrl} - ${responseMessage}`;
          store.dispatch(
            handleModuleFailure("login-401-invalid-auth", errorMessage, flowType)
          );
        }
      }

      if (!!logObj && isAppEnv()) {
        let errorMessage = JSON.parse(JSON.stringify(error));

        if (errorMessage?.message?.includes("401")) {
          if (tokenObj?.refreshToken) {
            tokenObj?.refreshToken();
          }
        }
        logObj.log(error);
      }
    
      return Promise.reject(error);
    } catch (error) {
      // __Dev__ is not defined or in used shared so it through the Error in Web App so commented the logger as of now
      // log API error on crashlytics
      if (!!logObj && isAppEnv()) {
        logObj.log(error);
      }
      return Promise.reject(error);
    } finally {
    }
  }
);

// If the outgoing request is using the brightspeed digital-id token and that
// token has reached its expiry, fetch a new one and swap it into the request's
// Authorization header before the request is sent.

// Shared across every parallel request so that N simultaneous callers with
// an expired token result in a single /digitalid refresh network call.
let inFlightRefresh = null;
// Remembers the digital-id token that was current BEFORE the last refresh
// completed. Lets us recognize a stale header captured by a caller just
// before another parallel refresh swapped state.data.access_token.
let previousDigitalIdToken = null;

async function refreshWebToken(config) {
  const env = getEnvironment();
  // Skip when the request has no auth header, isn't hitting the brightspeed
  // API host, or the web redux bridge hasn't been wired up yet.
  if (
    !config?.headers?.Authorization ||
    config?.url?.apiUrl !== env?.brightspeedApi ||
    !webRefreshTokenRedux?.webReduxStore
  ) {
    return;
  }
  const reduxStore = webRefreshTokenRedux.webReduxStore.getState();
  const tokenData = reduxStore?.brightspeedToken?.data;
  const storedAccessToken = tokenData?.access_token || tokenData?.id;
  if (!storedAccessToken) {
    return;
  }
  // Recognize the outgoing header as "our" digital-id token if it matches
  // either the current stored token OR the one that was current right
  // before the last refresh. Other Authorization values (amsToken.id,
  // user JWT, etc.) are left untouched.
  const outgoingAuth = config.headers.Authorization;
  const matchesCurrent = outgoingAuth === `Bearer ${storedAccessToken}`;
  const matchesPrevious =
    previousDigitalIdToken &&
    outgoingAuth === `Bearer ${previousDigitalIdToken}`;
  if (!matchesCurrent && !matchesPrevious) {
    return;
  }
  // Token is considered expired once the stamped expiry is in the past
  // (margin is already subtracted in the slice). Prefer the top-level
  // `tokenExpiresAt` (survives persist reliably) and fall back to the
  // legacy `data.expiryDate` for backwards compatibility.
  const expiryMs =
    reduxStore?.brightspeedToken?.tokenExpiresAt ??
    (tokenData?.expiryDate ? new Date(tokenData.expiryDate).getTime() : 0);
  const isExpired = !expiryMs || expiryMs <= Date.now();

  if (isExpired && webRefreshTokenRedux?.refreshToken) {
    // Dedupe parallel refreshes: all callers await the same promise, so
    // /digitalid is hit at most once per expiry window.
    if (!inFlightRefresh) {
      previousDigitalIdToken = storedAccessToken;
      inFlightRefresh = Promise.resolve()
        .then(() => webRefreshTokenRedux.refreshToken())
        .finally(() => {
          inFlightRefresh = null;
        });
    }
    await inFlightRefresh;
  }

  // Always pick up the latest digital-id from state. This closes the race
  // where a caller captured its headers before a parallel refresh completed
  // (outgoingAuth matched `previousDigitalIdToken` but is now stale).
  const refreshedData =
    webRefreshTokenRedux.webReduxStore.getState()?.brightspeedToken?.data;
  const refreshedToken =
    refreshedData?.access_token || refreshedData?.id;
  if (refreshedToken && outgoingAuth !== `Bearer ${refreshedToken}`) {
    config.headers.Authorization = `Bearer ${refreshedToken}`;
  }
}

export default instanceAxios;
